// frontend/src/features/landingPages/components/SectionOutlineItem.js
import React, { useState } from 'react';
import { getSectionType, FORM_FIELD_OPTIONS } from '../sectionCatalog';

/**
 * A single section in the outline list. Supports:
 *  - expand/collapse to edit the structural fields
 *  - move up / move down (reorder)
 *  - remove
 *  - informative content summary snippet in header
 *
 * UI-only: edits live in local component state in the parent view.
 */
/** Checkbox list of the supported form fields, stored as one label per line. */
function FormFieldPicker({ idPrefix, value, onChange }) {
  const chosen = new Set(
    String(value)
      .split('\n')
      .map((l) => l.trim().toLowerCase())
      .filter(Boolean)
  );
  const toggle = (label) => {
    const next = FORM_FIELD_OPTIONS.filter((o) =>
      o.label === label ? !chosen.has(o.label.toLowerCase()) : chosen.has(o.label.toLowerCase())
    );
    onChange(next.map((o) => o.label).join('\n'));
  };
  return (
    <div className="lp-form-field-picker" role="group" id={idPrefix} aria-label="Form fields">
      {FORM_FIELD_OPTIONS.map((o) => {
        const id = `${idPrefix}-${o.key}`;
        const checked = o.required || chosen.has(o.label.toLowerCase());
        return (
          <label key={o.key} htmlFor={id} className="lp-form-field-option">
            <input
              id={id}
              type="checkbox"
              checked={checked}
              disabled={o.required}
              onChange={() => toggle(o.label)}
            />
            <span>
              {o.label}
              {o.required ? ' (always included)' : ''}
            </span>
          </label>
        );
      })}
    </div>
  );
}

export default function SectionOutlineItem({
  section,
  index,
  total,
  onChangeContent,
  onMoveUp,
  onMoveDown,
  onRemove,
}) {
  const [expanded, setExpanded] = useState(false);
  const def = getSectionType(section.type);

  const handleFieldChange = (key, value) => {
    onChangeContent(section.id, { ...section.content, [key]: value });
  };

  const snippet = (() => {
    if (!section.content) return '';
    if (section.content.heading) return section.content.heading;
    if (section.content.quote) {
      const q = String(section.content.quote).trim();
      return `“${q.slice(0, 42)}${q.length > 42 ? '…' : ''}”`;
    }
    if (section.content.fields) {
      const count = String(section.content.fields).split('\n').filter(Boolean).length;
      return `${count} form field${count === 1 ? '' : 's'}`;
    }
    return '';
  })();

  return (
    <li className="lp-outline-item">
      <div className="lp-outline-head">
        <span className="lp-outline-pos" aria-hidden="true">
          {index + 1}
        </span>
        <span className="lp-outline-icon" aria-hidden="true">
          <i className={def ? def.icon : 'fas fa-square'}></i>
        </span>
        <div className="lp-outline-title-meta">
          <span className="lp-outline-label">{section.label}</span>
          {snippet && <span className="lp-outline-snippet">{snippet}</span>}
        </div>

        <div className="lp-outline-actions">
          <button
            type="button"
            className="lp-icon-btn"
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            aria-label={`Move ${section.label} up`}
            title="Move up"
          >
            <i className="fas fa-arrow-up"></i>
          </button>
          <button
            type="button"
            className="lp-icon-btn"
            onClick={() => onMoveDown(index)}
            disabled={index === total - 1}
            aria-label={`Move ${section.label} down`}
            title="Move down"
          >
            <i className="fas fa-arrow-down"></i>
          </button>
          <button
            type="button"
            className={`lp-icon-btn ${expanded ? 'active' : ''}`}
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Edit'} ${section.label}`}
            title={expanded ? 'Collapse' : 'Edit'}
          >
            <i className={`fas ${expanded ? 'fa-chevron-up' : 'fa-pen'}`}></i>
          </button>
          <button
            type="button"
            className="lp-icon-btn lp-icon-btn-danger"
            onClick={() => onRemove(section.id)}
            aria-label={`Remove ${section.label}`}
            title="Remove"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      {expanded && def && (
        <div className="lp-outline-body">
          <div className="lp-outline-body-header">
            <span className="lp-outline-body-badge">
              <i className="fas fa-sliders" aria-hidden="true"></i>
              Edit {section.label} Content
            </span>
          </div>
          {def.fields.map((field) => (
            <div className="lp-field" key={field.key}>
              <label className="lp-field-label" htmlFor={`${section.id}-${field.key}`}>
                {field.label}
              </label>
              {field.type === 'formFields' ? (
                <FormFieldPicker
                  idPrefix={`${section.id}-${field.key}`}
                  value={section.content[field.key] || ''}
                  onChange={(v) => handleFieldChange(field.key, v)}
                />
              ) : field.type === 'textarea' ? (
                <textarea
                  id={`${section.id}-${field.key}`}
                  className="lp-field-input"
                  rows={3}
                  value={section.content[field.key] || ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                />
              ) : (
                <input
                  id={`${section.id}-${field.key}`}
                  type="text"
                  className="lp-field-input"
                  value={section.content[field.key] || ''}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}
